import type { AiModel } from '@/types';
import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Tooltip
} from '@heroui/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { RiAddLine, RiDeleteBinLine, RiRefreshLine, RiSearchLine, RiSpeedLine } from '@remixicon/react';
import { toast } from 'react-toastify';

import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { ColumnsToggleDropdown, FilterSelect, TableFilters, TablePage } from '@/components/table';
import { useAiModelsFilters } from '@/hooks/ai-models';
import { useVisibleColumns } from '@/hooks/table';
import {
  aiModelsListParamsAtom,
  aiModelsQueryAtom,
  createAiModelAtom,
  deleteAiModelAtom,
  fetchAllAiModelIds,
  latencyTestAiModelAtom,
  reorderAiModelsAtom,
  syncAiModelsAtom,
  updateAiModelAtom
} from '@/store/aiModels';

type AiModelRow = AiModel & { rank: number; isRowTesting?: boolean; isRowSelected?: boolean };

function formatUsdPerMillion(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'n/a';
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 'n/a';
  if (numeric === 0) return '$0';

  const fixed = numeric < 0.01 ? numeric.toFixed(6) : numeric.toFixed(4);
  const compact = fixed.replace(/\.?0+$/, '');

  return `$${compact}`;
}

function formatContextLength(value: number | null | undefined): string {
  if (value === null || value === undefined || value <= 0) return 'n/a';
  return value.toLocaleString();
}

function RankInput({
  rank,
  max,
  disabled,
  onCommit
}: {
  rank: number;
  max: number;
  disabled?: boolean;
  onCommit: (nextRank: number) => void;
}) {
  const [value, setValue] = React.useState(String(rank));

  React.useEffect(() => {
    setValue(String(rank));
  }, [rank]);

  const commit = () => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > max) {
      setValue(String(rank));
      return;
    }
    if (parsed === rank) return;
    onCommit(parsed);
  };

  return (
    <Input
      aria-label="Priority rank"
      className="w-16"
      classNames={{
        input: 'text-center text-tiny font-semibold',
        inputWrapper: 'h-7 min-h-7'
      }}
      isDisabled={disabled}
      max={max}
      min={1}
      size="sm"
      type="number"
      value={value}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
      }}
      onValueChange={setValue}
    />
  );
}

export default function AiModelsPage() {
  const { data, isLoading, isError, error } = useAtomValue(aiModelsQueryAtom);
  const [, setListParams] = useAtom(aiModelsListParamsAtom);
  const syncModels = useSetAtom(syncAiModelsAtom);
  const reorderModels = useSetAtom(reorderAiModelsAtom);
  const updateModel = useSetAtom(updateAiModelAtom);
  const createModel = useSetAtom(createAiModelAtom);
  const deleteModel = useSetAtom(deleteAiModelAtom);
  const runLatencyTest = useSetAtom(latencyTestAiModelAtom);

  const {
    params,
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    handlePerPageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  } = useAiModelsFilters();

  const [isSyncing, setIsSyncing] = React.useState(false);
  const [isReordering, setIsReordering] = React.useState(false);
  const [testingId, setTestingId] = React.useState<number | null>(null);
  const [isBatchTesting, setIsBatchTesting] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<AiModel | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState(searchFields.search || '');
  const [activeFilter, setActiveFilter] = React.useState<'all' | 'true' | 'false'>(searchFields.is_active);
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>({
    column: 'rank',
    direction: 'ascending'
  });
  const [createForm, setCreateForm] = React.useState({
    slug: '',
    openrouter_id: '',
    display_name: ''
  });

  const meta = data?.meta;
  const totalModels = meta?.total ?? 0;

  React.useEffect(() => {
    setSearchInput(searchFields.search || '');
    setActiveFilter(searchFields.is_active);
  }, [searchFields.search, searchFields.is_active]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === searchFields.search) return;
      setSearchFields((prev) => ({ ...prev, search: searchInput }));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput, searchFields.search, setSearchFields]);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor({ column: 'rank', direction: 'ascending' });
      return;
    }

    const direction = sortParam.startsWith('-') ? 'descending' : 'ascending';
    const column = sortParam.replace(/^-/, '');
    const uiColumn = column === 'sort_order' ? 'rank' : column;
    setSortDescriptor({ column: uiColumn, direction });
  }, [sortParam]);

  const pageModels = React.useMemo<AiModel[]>(() => data?.data ?? [], [data]);

  const tableRows = React.useMemo<AiModelRow[]>(
    () =>
      pageModels.map((model) => ({
        ...model,
        rank: model.sort_order + 1,
        isRowTesting: testingId === model.id,
        isRowSelected: selectedIds.has(model.id)
      })),
    [pageModels, testingId, selectedIds]
  );

  const pageIds = React.useMemo(() => pageModels.map((model) => model.id), [pageModels]);

  const selectedCount = selectedIds.size;
  const pageSelectedCount = React.useMemo(
    () => pageIds.filter((id) => selectedIds.has(id)).length,
    [pageIds, selectedIds]
  );
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const showingFrom = totalModels > 0 ? (meta!.current_page - 1) * meta!.per_page + 1 : 0;
  const showingTo = meta ? Math.min(meta.current_page * meta.per_page, meta.total) : 0;

  const columns = React.useMemo(
    () => [
      { name: 'Select', uid: 'select', align: 'center' as const },
      { name: 'Rank', uid: 'rank', sortable: true, align: 'center' as const },
      { name: 'Model', uid: 'display_name', sortable: true },
      { name: 'Prompt / 1M', uid: 'pricing_prompt', sortable: true },
      { name: 'Completion / 1M', uid: 'pricing_completion', sortable: true },
      { name: 'Context', uid: 'context_length', sortable: true },
      { name: 'Latency', uid: 'last_latency_ms', sortable: true },
      { name: 'Active', uid: 'is_active', sortable: true, align: 'center' as const },
      { name: 'Actions', uid: 'actions', align: 'end' as const }
    ],
    []
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(
    columns,
    columns.map((column) => column.uid)
  );

  const triggerRefresh = React.useCallback(() => {
    setListParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setListParams]);

  const toggleSelected = (id: number, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAllPage = (selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (selected) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      setSortDescriptor(descriptor);
      const column = descriptor.column?.toString();
      if (!column) {
        setSortParam(null);
        return;
      }
      const apiColumn = column === 'rank' ? 'sort_order' : column;
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${apiColumn}`);
    },
    [setSortParam]
  );

  const handleClearFilters = React.useCallback(() => {
    setSearchInput('');
    setActiveFilter('all');
    clearFilters();
  }, [clearFilters]);

  const tableHeaderColumns = React.useMemo(
    () =>
      headerColumns.map((column) => {
        if (column.uid !== 'select') return column;

        return {
          ...column,
          name: (
            <Checkbox
              aria-label="Select all on page"
              isDisabled={pageIds.length === 0 || isBatchTesting}
              isIndeterminate={pageSelectedCount > 0 && !allPageSelected}
              isSelected={allPageSelected}
              size="sm"
              onValueChange={toggleSelectAllPage}
            />
          )
        };
      }),
    [allPageSelected, headerColumns, isBatchTesting, pageIds.length, pageSelectedCount]
  );

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await syncModels();
      toast.success('OpenRouter models synced');
    } catch {
      toast.error('Failed to sync OpenRouter models');
    } finally {
      setIsSyncing(false);
    }
  };

  const moveToRank = async (modelId: number, nextRank: number) => {
    try {
      setIsReordering(true);
      const ids = await fetchAllAiModelIds();
      const fromIndex = ids.indexOf(modelId);
      if (fromIndex < 0) return;

      const targetIndex = Math.min(Math.max(nextRank - 1, 0), ids.length - 1);
      if (fromIndex === targetIndex) return;

      const next = [...ids];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(targetIndex, 0, moved);
      await reorderModels(next);
      toast.success('Model priority order saved');
    } catch {
      toast.error('Failed to save model order');
      triggerRefresh();
    } finally {
      setIsReordering(false);
    }
  };

  const toggleActive = async (model: AiModel) => {
    try {
      await updateModel({ id: model.id, payload: { is_active: !model.is_active } });
    } catch {
      toast.error('Failed to update model');
    }
  };

  const handleLatencyTest = async (model: AiModel) => {
    setTestingId(model.id);
    try {
      const response = await runLatencyTest(model.id);
      const latencyMs = (response as { data?: { test?: { latency_ms?: number } } })?.data?.test?.latency_ms;
      toast.success(`Latency test: ${latencyMs ?? 'n/a'} ms`);
      triggerRefresh();
    } catch {
      toast.error('Latency test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleBatchLatencyTest = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setIsBatchTesting(true);
    let passed = 0;
    let failed = 0;

    try {
      for (const id of ids) {
        setTestingId(id);
        try {
          await runLatencyTest(id);
          passed += 1;
        } catch {
          failed += 1;
        }
      }
      toast.success(`Batch test finished: ${passed} passed, ${failed} failed`);
      triggerRefresh();
    } finally {
      setTestingId(null);
      setIsBatchTesting(false);
    }
  };

  const handleCreate = async () => {
    try {
      await createModel({
        ...createForm,
        is_active: false,
        sort_order: totalModels
      });
      setIsCreateOpen(false);
      setCreateForm({ slug: '', openrouter_id: '', display_name: '' });
      toast.success('AI model created');
    } catch {
      toast.error('Failed to create AI model');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setIsDeleting(true);
      await deleteModel(pendingDelete.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(pendingDelete.id);
        return next;
      });
      toast.success('AI model deleted');
      setPendingDelete(null);
    } catch {
      toast.error('Failed to delete model');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderCell = React.useCallback(
    (model: AiModelRow, columnKey: React.Key) => {
      switch (columnKey) {
        case 'select':
          return (
            <Checkbox
              aria-label={`Select ${model.display_name}`}
              isDisabled={isBatchTesting}
              isSelected={Boolean(model.isRowSelected)}
              size="sm"
              onValueChange={(selected) => toggleSelected(model.id, selected)}
            />
          );
        case 'rank':
          return (
            <RankInput
              disabled={isReordering || isBatchTesting}
              max={totalModels}
              rank={model.rank}
              onCommit={(nextRank) => void moveToRank(model.id, nextRank)}
            />
          );
        case 'display_name':
          return (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold">{model.display_name}</span>
              <span className="text-default-400 truncate text-xs">{model.openrouter_id}</span>
            </div>
          );
        case 'pricing_prompt':
          return <span className="text-sm">{formatUsdPerMillion(model.pricing_prompt)}</span>;
        case 'pricing_completion':
          return <span className="text-sm">{formatUsdPerMillion(model.pricing_completion)}</span>;
        case 'context_length':
          return <span className="text-sm">{formatContextLength(model.context_length)}</span>;
        case 'last_latency_ms':
          return (
            <div className="flex flex-col">
              <span className="text-sm">{model.last_latency_ms ?? 'n/a'} ms</span>
              {model.last_tested_at ? (
                <span className="text-default-400 text-[11px]">
                  {new Date(model.last_tested_at).toLocaleString()}
                </span>
              ) : null}
            </div>
          );
        case 'is_active':
          return (
            <div className="flex justify-center">
              <Switch
                isSelected={model.is_active}
                size="sm"
                onValueChange={() => void toggleActive(model)}
              />
            </div>
          );
        case 'actions': {
          const isTesting = Boolean(model.isRowTesting);

          return (
            <div className="flex items-center justify-end gap-1">
              <PermissionGuard permission="AiModel.update">
                <Tooltip content={isTesting ? 'Testing…' : 'Run latency test'}>
                  <span className="inline-flex">
                    <Button
                      aria-busy={isTesting}
                      isDisabled={isBatchTesting && !isTesting}
                      isIconOnly
                      isLoading={isTesting}
                      size="sm"
                      variant="light"
                      onPress={() => void handleLatencyTest(model)}
                    >
                      <RiSpeedLine className="h-4 w-4" />
                    </Button>
                  </span>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="AiModel.destroy">
                <Tooltip content="Delete model">
                  <span className="inline-flex">
                    <Button
                      color="danger"
                      isDisabled={isBatchTesting || isTesting}
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => setPendingDelete(model)}
                    >
                      <RiDeleteBinLine className="h-4 w-4" />
                    </Button>
                  </span>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [isBatchTesting, isReordering, totalModels]
  );

  return (
    <>
      <TablePage
        columns={columns}
        currentPage={meta?.current_page}
        data={tableRows}
        emptyMessage={
          totalModels === 0 && !hasAnyFilter
            ? 'No models yet. Sync from OpenRouter or add one manually.'
            : 'No models match the current search/filter.'
        }
        error={error}
        errorMessage="Failed to load AI models"
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={handleClearFilters}
            onRefresh={triggerRefresh}
            rightSection={
              <div className="flex items-center gap-2">
                <span className="text-default-500 self-center text-sm">
                  {totalModels > 0
                    ? `Showing ${showingFrom}–${showingTo} of ${totalModels}`
                    : 'No models'}
                </span>
                {selectedCount > 0 && (
                  <PermissionGuard permission="AiModel.update">
                    <Button
                      color="primary"
                      isLoading={isBatchTesting}
                      size="sm"
                      startContent={<RiSpeedLine className="h-4 w-4" />}
                      variant="flat"
                      onPress={() => void handleBatchLatencyTest()}
                    >
                      Test selected ({selectedCount})
                    </Button>
                  </PermissionGuard>
                )}
              </div>
            }
          >
            <ColumnsToggleDropdown
              allColumns={columns.map((column) => ({ uid: column.uid, name: column.name }))}
              selected={visibleColumns}
              onChange={setVisibleColumns}
            />
            <Input
              className="w-72"
              label="Search"
              placeholder="Name, OpenRouter ID, or slug"
              size="sm"
              startContent={<RiSearchLine className="text-default-400 h-4 w-4" />}
              value={searchInput}
              onValueChange={setSearchInput}
            />
            <FilterSelect
              className="w-40"
              label="Status"
              options={[
                { key: 'all', label: 'All' },
                { key: 'true', label: 'Active' },
                { key: 'false', label: 'Inactive' }
              ]}
              value={activeFilter}
              onChange={(value) => {
                const next = value as 'all' | 'true' | 'false';
                setActiveFilter(next);
                setSearchFields((prev) => ({ ...prev, is_active: next }));
              }}
            />
          </TableFilters>
        }
        headerColumns={tableHeaderColumns}
        isError={isError}
        isLoading={isLoading}
        perPage={params.per_page}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        title="AI Models"
        titleActions={
          <div className="flex flex-wrap gap-2">
            <PermissionGuard permission="AiModel.store">
              <Button
                color="success"
                size="sm"
                startContent={<RiAddLine className="h-4 w-4" />}
                variant="flat"
                onPress={() => setIsCreateOpen(true)}
              >
                Add model
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="AiModel.update">
              <Button
                isLoading={isSyncing}
                size="sm"
                startContent={<RiRefreshLine className="h-4 w-4" />}
                variant="flat"
                onPress={() => void handleSync()}
              >
                Sync OpenRouter
              </Button>
            </PermissionGuard>
          </div>
        }
        totalPages={meta?.last_page}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onSortChange={handleSortChange}
      />

      <Modal isOpen={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Add AI model</ModalHeader>
              <ModalBody className="gap-3">
                <Input
                  label="Display name"
                  value={createForm.display_name}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, display_name: value }))}
                />
                <Input
                  label="Slug"
                  value={createForm.slug}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, slug: value }))}
                />
                <Input
                  label="OpenRouter ID"
                  value={createForm.openrouter_id}
                  onValueChange={(value) => setCreateForm((prev) => ({ ...prev, openrouter_id: value }))}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={() => void handleCreate()}>
                  Create
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <ConfirmDeleteModal
        confirmText="Delete model"
        isLoading={isDeleting}
        isOpen={pendingDelete !== null}
        message={
          pendingDelete
            ? `Are you sure you want to delete the model "${pendingDelete.display_name}"?`
            : 'Are you sure you want to delete this model?'
        }
        title="Delete AI model"
        onConfirm={() => void confirmDelete()}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      />
    </>
  );
}
